import React, { useState, useMemo } from 'react';
import { Box, Paper, Typography, Grid, Chip, TextField, Table, TableBody, TableCell, TableHead, TableRow } from '@mui/material';

const MyLeaveTab = ({ balances, myRequests }) => {
  // Dynamically calculate today, current year, and yesterday's date
  const today = new Date();
  const currentYear = today.getFullYear();
  
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);
  
  // Format yesterday safely to YYYY-MM-DD in local time
  const yyyy = yesterday.getFullYear();
  const mm = String(yesterday.getMonth() + 1).padStart(2, '0');
  const dd = String(yesterday.getDate()).padStart(2, '0');
  const yesterdayStr = `${yyyy}-${mm}-${dd}`;

  // Set the default range: Jan 1st of current year -> Yesterday
  const [dateRange, setDateRange] = useState({ start: `${currentYear}-01-01`, end: yesterdayStr });

  const getMyBalanceDetails = (type) => {
    const bal = balances.find(b => b.leave_type === type);
    if (!bal) return { days: 0, expiry: null };
    return { days: bal.total_days - bal.used_days, expiry: bal.expiry_date };
  };

  const historyCounts = useMemo(() => {
    const counts = { CL: 0, NH: 0, 'HALF-DAY': 0, WO: 0, AL: 0, LOP: 0, DLOP: 0, ML: 0, EL: 0, CO: 0 };
    myRequests.forEach(req => {
      if (req.status === 'APPROVED' && req.start_date >= dateRange.start && req.start_date <= dateRange.end) {
        if (counts[req.leave_type] !== undefined) {
          counts[req.leave_type] += Number(req.days) || 0;
        }
      }
    });
    return counts;
  }, [myRequests, dateRange]);

  return (
    <Box sx={{ textAlign: 'left' }}>
      <Typography variant="h6" color="primary" sx={{ fontWeight: 'bold', mb: 2 }}>My Available Balances</Typography>
      
      <Grid container spacing={2}>
        {['CL', 'ML', 'AL', 'EL', 'NH', 'CO'].map(type => {
          const bData = getMyBalanceDetails(type);
          return (
            <Grid item xs={12} sm={4} md={2} key={type}>
              <Paper elevation={1} sx={{ p: 2, textAlign: 'left', borderRadius: 2, borderTop: type === 'NH' ? '4px solid #ff9800' : '4px solid #1976d2' }}>
                <Typography variant="subtitle2" color="textSecondary" sx={{ fontWeight: 'bold' }}>{type}</Typography>
                <Typography variant="h5" color="primary" sx={{ fontWeight: 'bold', my: 0.5 }}>{bData.days}</Typography>
                <Typography variant="caption" color="textSecondary" sx={{ display: 'block' }}>Available</Typography>
                {/* DO NOT show expiry if days is 0 */}
                {bData.days > 0 && bData.expiry && (
                  <Chip size="small" label={`Exp: ${bData.expiry}`} color="warning" variant="outlined" sx={{ mt: 1, fontSize: '0.65rem', height: 20 }} />
                )}
              </Paper>
            </Grid>
          );
        })}
      </Grid>

      <Paper elevation={2} sx={{ p: 3, mt: 4, borderRadius: 2, textAlign: 'left' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h6" color="textPrimary" sx={{ fontWeight: 'bold' }}>My Leave History</Typography>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField size="small" type="date" label="Start Date" InputLabelProps={{ shrink: true }} value={dateRange.start} onChange={e => setDateRange({...dateRange, start: e.target.value})} />
            <TextField size="small" type="date" label="End Date" InputLabelProps={{ shrink: true }} value={dateRange.end} onChange={e => setDateRange({...dateRange, end: e.target.value})} />
          </Box>
        </Box>
        
        <Table size="small">
          <TableHead sx={{ bgcolor: '#f1f5f9' }}>
            <TableRow>
              {Object.keys(historyCounts).map(type => (
                <TableCell key={type} align="center" sx={{ fontWeight: 'bold', color: '#475569' }}>{type}</TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            <TableRow>
              {Object.keys(historyCounts).map(type => (
                <TableCell key={type} align="center">
                  <Typography variant="body2" sx={{ fontWeight: historyCounts[type] > 0 ? 'bold' : 'normal', color: historyCounts[type] > 0 ? 'primary.main' : 'text.secondary' }}>
                    {historyCounts[type]}
                  </Typography>
                </TableCell>
              ))}
            </TableRow>
          </TableBody>
        </Table>
      </Paper>
    </Box>
  );
};

export default MyLeaveTab;